import { zodResolver } from '@hookform/resolvers/zod';
import {
  usePostApiVirtualDevices,
  usePutApiVirtualDevicesId,
  useListDevices,
  getListDevicesQueryKey,
  getGetApiVirtualDevicesQueryKey,
  type DevicesDevice,
  type DevicesDeviceState,
  type VirtualdevicesVirtualDeviceDTO,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  Layers,
  GitBranch,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Search,
  Power,
  Lightbulb,
  Fan,
  Tv,
  Speaker,
  Blinds,
  Copy,
  ArrowLeft,
  Check,
  X,
} from 'lucide-react';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  useForm,
  Controller,
  useFieldArray,
  useWatch,
  type Control,
  type FieldErrors,
} from 'react-hook-form';
import { z } from 'zod';

import { Modal, SlideOutPanel, Button, TextField, Toggle, Badge, Select } from '@/components/ui';
import { cn } from '@/components/ui/utils';

interface VirtualDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualDevice?: VirtualdevicesVirtualDeviceDTO;
}

type VirtualDeviceKind = 'combined_switch' | 'routed';

const DEVICE_ICONS: Record<string, React.ElementType> = {
  light: Lightbulb,
  switch: Power,
  fan: Fan,
  tv: Tv,
  speaker: Speaker,
  window_covering: Blinds,
};

// Property paths by device type
const DEVICE_PROPERTIES: Record<string, string[]> = {
  switch: ['switch.on', 'switch.position'],
  light: ['light.on', 'light.brightness', 'light.color'],
  fan: ['fan.on', 'fan.intensity', 'fan.swivel'],
  window_covering: ['windowCovering.closedPercentage'],
  speaker: ['speaker.on', 'speaker.volume', 'speaker.source', 'speaker.playState'],
  tv: ['tv.on', 'tv.volume', 'tv.muted', 'tv.source', 'tv.playState'],
};

// Command names from backend
const DEVICE_COMMANDS = [
  'volumeUp',
  'volumeDown',
  'channelUp',
  'channelDown',
  'toggleMute',
  'toggleOnOff',
  'playPause',
  'stop',
  'next',
  'previous',
];

export function VirtualDeviceModal({ isOpen, onClose, virtualDevice }: VirtualDeviceModalProps) {
  const [selectedKind, setSelectedKind] = useState<VirtualDeviceKind | null>(null);
  const isEditing = !!virtualDevice;
  const editingKind = virtualDevice?.kind as VirtualDeviceKind | undefined;

  const handleClose = useCallback(() => {
    setSelectedKind(null);
    onClose();
  }, [onClose]);

  const handleSuccess = useCallback(() => {
    setSelectedKind(null);
    onClose();
  }, [onClose]);

  const handleBack = useCallback(() => {
    setSelectedKind(null);
  }, []);

  if (!isOpen) return null;

  // In edit mode, go directly to the appropriate form based on the device kind
  const activeKind = isEditing ? editingKind : selectedKind;

  // Kind-specific creation/edit form (with back button for type selection in create mode)
  if (activeKind === 'combined_switch') {
    return (
      <CombinedSwitchForm
        isOpen={isOpen}
        onClose={handleClose}
        onSuccess={handleSuccess}
        onBack={isEditing ? undefined : handleBack}
        virtualDevice={virtualDevice}
      />
    );
  }

  if (activeKind === 'routed') {
    return (
      <RoutedDeviceForm
        isOpen={isOpen}
        onClose={handleClose}
        onSuccess={handleSuccess}
        onBack={isEditing ? undefined : handleBack}
        virtualDevice={virtualDevice}
      />
    );
  }

  // Type selection inside SlideOutPanel (only for create mode)
  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Virtual Device"
      subtitle="Choose the type of virtual device to create"
      size="lg"
    >
      <div className="space-y-3">
        <button
          onClick={() => setSelectedKind('combined_switch')}
          className="flex w-full items-start gap-4 rounded-xl border border-gray-200 p-4 text-left transition-colors hover:border-blue-500 hover:bg-blue-50/50 dark:border-white/[0.06] dark:hover:border-blue-500/50 dark:hover:bg-blue-500/5"
        >
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400">
            <Layers className="h-6 w-6" />
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-white">Combined Switch</div>
            <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
              Create a multi-position switch that controls multiple devices at once. Each position
              triggers a set of device states.
            </p>
          </div>
        </button>

        <button
          onClick={() => setSelectedKind('routed')}
          className="flex w-full items-start gap-4 rounded-xl border border-gray-200 p-4 text-left transition-colors hover:border-blue-500 hover:bg-blue-50/50 dark:border-white/[0.06] dark:hover:border-blue-500/50 dark:hover:bg-blue-500/5"
        >
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
            <GitBranch className="h-6 w-6" />
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-white">Routed Device</div>
            <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
              Create a device that routes properties and commands to different underlying devices.
              Perfect for combining multiple devices into one virtual device.
            </p>
          </div>
        </button>
      </div>
    </SlideOutPanel>
  );
}

// Keep the old export name for backwards compatibility
export const CreateVirtualDeviceModal = VirtualDeviceModal;

// Combined Switch Form
const combinedSwitchSchema = z.object({
  humanId: z
    .string()
    .min(1, 'Required')
    .regex(/^[a-z0-9_-]+$/, 'Only lowercase letters, numbers, hyphens, and underscores'),
  name: z.string().min(1, 'Required'),
  positions: z
    .array(
      z.object({
        id: z.string().min(1, 'Required'),
        label: z.string().min(1, 'Required'),
        actions: z.array(
          z.object({
            deviceId: z.string().min(1),
            on: z.boolean(),
          })
        ),
      })
    )
    .min(2, 'At least 2 positions required'),
});

type CombinedSwitchFormData = z.infer<typeof combinedSwitchSchema>;

interface FormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onBack?: () => void;
  virtualDevice?: VirtualdevicesVirtualDeviceDTO;
}

function CombinedSwitchForm({ isOpen, onClose, onSuccess, onBack, virtualDevice }: FormProps) {
  const queryClient = useQueryClient();
  const [expandedPosition, setExpandedPosition] = useState<number | null>(0);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isEditing = !!virtualDevice;

  useEffect(() => {
    if (showAddDeviceModal !== null) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [showAddDeviceModal]);

  const { data: devicesData, isLoading: devicesLoading } = useListDevices();
  const devices = useMemo(() => devicesData?.devices ?? [], [devicesData?.devices]);

  const switchableDevices = useMemo(() => {
    return devices.filter(
      (d) =>
        d.type === 'switch' ||
        d.type === 'light' ||
        d.state?.switch?.on !== undefined ||
        d.state?.light?.on !== undefined
    );
  }, [devices]);

  const createMutation = usePostApiVirtualDevices();
  const updateMutation = usePutApiVirtualDevicesId();

  // Parse existing config for edit mode
  const existingConfig = useMemo(() => {
    if (!virtualDevice?.config) return null;
    const config = virtualDevice.config as {
      positions?: Array<{
        id: string;
        label: string;
        actions?: Array<{ deviceId: string; state?: { switch?: { on?: boolean } } }>;
      }>;
    };
    return config;
  }, [virtualDevice?.config]);

  // Build default values from existing device or use empty defaults
  const defaultValues = useMemo((): CombinedSwitchFormData => {
    if (isEditing && existingConfig?.positions) {
      return {
        humanId: virtualDevice.humanId ?? '',
        name: virtualDevice.name ?? '',
        positions: existingConfig.positions.map((pos) => ({
          id: pos.id,
          label: pos.label,
          actions: (pos.actions ?? []).map((action) => ({
            deviceId: action.deviceId,
            on: action.state?.switch?.on ?? false,
          })),
        })),
      };
    }
    return {
      humanId: '',
      name: '',
      positions: [
        { id: 'off', label: 'Off', actions: [] },
        { id: 'on', label: 'On', actions: [] },
      ],
    };
  }, [isEditing, existingConfig, virtualDevice]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<CombinedSwitchFormData>({
    resolver: zodResolver(combinedSwitchSchema),
    defaultValues,
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'positions',
  });

  const positions = watch('positions');

  const getUsedDeviceIds = useCallback(
    (positionIndex: number) => {
      const position = positions[positionIndex];
      if (!position) return new Set<string>();
      return new Set(position.actions.map((a) => a.deviceId));
    },
    [positions]
  );

  const filteredDevices = useMemo(() => {
    if (showAddDeviceModal === null) return [];
    const usedIds = getUsedDeviceIds(showAddDeviceModal);
    let available = switchableDevices.filter((d) => d.id && !usedIds.has(d.id));
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      available = available.filter(
        (d) =>
          d.name?.toLowerCase().includes(query) ||
          d.humanId?.toLowerCase().includes(query) ||
          d.provider?.toLowerCase().includes(query)
      );
    }
    return available;
  }, [showAddDeviceModal, switchableDevices, getUsedDeviceIds, searchQuery]);

  const handleAddAction = useCallback(
    (positionIndex: number, device: DevicesDevice) => {
      const position = positions[positionIndex];
      if (!position || !device.id) return;
      const defaultOn = position.id !== 'off';
      update(positionIndex, {
        ...position,
        actions: [...position.actions, { deviceId: device.id, on: defaultOn }],
      });
      setShowAddDeviceModal(null);
      setSearchQuery('');
    },
    [positions, update]
  );

  const handleRemoveAction = useCallback(
    (positionIndex: number, actionIndex: number) => {
      const position = positions[positionIndex];
      if (!position) return;
      update(positionIndex, {
        ...position,
        actions: position.actions.filter((_, i) => i !== actionIndex),
      });
    },
    [positions, update]
  );

  const handleToggleAction = useCallback(
    (positionIndex: number, actionIndex: number, on: boolean) => {
      const position = positions[positionIndex];
      if (!position) return;
      update(positionIndex, {
        ...position,
        actions: position.actions.map((a, i) => (i === actionIndex ? { ...a, on } : a)),
      });
    },
    [positions, update]
  );

  const handleDuplicatePosition = useCallback(
    (positionIndex: number) => {
      const position = positions[positionIndex];
      if (!position) return;
      const newPosition = {
        id: `${position.id}_copy`,
        label: `${position.label} (Copy)`,
        actions: [...position.actions.map((a) => ({ ...a }))],
      };
      append(newPosition);
      setExpandedPosition(fields.length);
    },
    [positions, append, fields.length]
  );

  const getDeviceById = useCallback(
    (deviceId: string) => devices.find((d) => d.id === deviceId),
    [devices]
  );

  const onSubmit = async (data: CombinedSwitchFormData) => {
    const config = {
      positions: data.positions.map((pos) => ({
        id: pos.id,
        label: pos.label,
        actions: pos.actions.map((action) => ({
          deviceId: action.deviceId,
          state: {
            switch: { on: action.on },
          } as DevicesDeviceState,
        })),
      })),
    };

    try {
      if (isEditing && virtualDevice?.id) {
        await updateMutation.mutateAsync({
          id: virtualDevice.id,
          data: {
            name: data.name,
            config,
          },
        });
      } else {
        await createMutation.mutateAsync({
          data: {
            humanId: data.humanId,
            name: data.name,
            type: 'switch',
            kind: 'combined_switch',
            config,
          },
        });
      }
      queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetApiVirtualDevicesQueryKey() });
      onSuccess();
    } catch {
      // Error handled by mutation state
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isError = createMutation.isError || updateMutation.isError;

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Combined Switch' : 'Create Combined Switch'}
      subtitle={
        isEditing
          ? 'Modify positions and the devices each position controls'
          : 'Define positions and the devices each position controls'
      }
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit(onSubmit)} loading={isPending}>
            {isEditing ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      {onBack && (
        <div className="mb-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to type selection
          </button>
        </div>
      )}

      <form className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Human ID"
            {...register('humanId')}
            placeholder="living-room-combo"
            error={errors.humanId?.message}
            hint={isEditing ? 'Cannot be changed' : 'Unique identifier (lowercase, no spaces)'}
            disabled={isEditing}
          />
          <TextField
            label="Name"
            {...register('name')}
            placeholder="Living Room Combo Switch"
            error={errors.name?.message}
          />
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                Positions
              </label>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-500">
                Define each switch position and which devices it controls
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                append({
                  id: `position_${fields.length + 1}`,
                  label: `Position ${fields.length + 1}`,
                  actions: [],
                })
              }
              leftIcon={<Plus className="h-3.5 w-3.5" />}
            >
              Add Position
            </Button>
          </div>

          {errors.positions?.message && (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
              <AlertCircle className="h-4 w-4" />
              {errors.positions.message}
            </div>
          )}

          <div className="space-y-2">
            {fields.map((field, index) => {
              const isExpanded = expandedPosition === index;
              const position = positions[index];
              const actionCount = position?.actions?.length ?? 0;

              return (
                <div
                  key={field.id}
                  className="overflow-hidden rounded-xl border border-gray-200 dark:border-white/[0.06]"
                >
                  <div
                    className={cn(
                      'flex items-center gap-3 px-4 py-3',
                      isExpanded && 'border-b border-gray-200 dark:border-white/[0.06]'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedPosition(isExpanded ? null : index)}
                      className="flex flex-1 items-center gap-3 text-left"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {position?.label || `Position ${index + 1}`}
                          </span>
                          <Badge variant="default" size="sm">
                            {actionCount} device{actionCount !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-zinc-500">
                          ID: {position?.id}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDuplicatePosition(index)}
                      className="rounded p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-500 dark:hover:bg-blue-500/10"
                      title="Duplicate position"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {fields.length > 2 && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="bg-gray-50/50 p-4 dark:bg-white/[0.02]">
                      <div className="mb-4 grid gap-3 sm:grid-cols-2">
                        <Controller
                          control={control}
                          name={`positions.${index}.id`}
                          render={({ field }) => (
                            <TextField
                              label="Position ID"
                              {...field}
                              placeholder="off"
                              error={errors.positions?.[index]?.id?.message}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name={`positions.${index}.label`}
                          render={({ field }) => (
                            <TextField
                              label="Label"
                              {...field}
                              placeholder="Off"
                              error={errors.positions?.[index]?.label?.message}
                            />
                          )}
                        />
                      </div>

                      <div>
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                            Devices
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAddDeviceModal(index)}
                            leftIcon={<Plus className="h-3.5 w-3.5" />}
                          >
                            Add Device
                          </Button>
                        </div>

                        {position?.actions?.length === 0 ? (
                          <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center dark:border-white/[0.08]">
                            <p className="text-sm text-gray-500 dark:text-zinc-500">
                              No devices added. Add devices to control when this position is
                              selected.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {position?.actions?.map((action, actionIndex) => {
                              const device = getDeviceById(action.deviceId);
                              if (!device) return null;
                              const Icon = DEVICE_ICONS[device.type ?? 'switch'] ?? Power;

                              return (
                                <div
                                  key={action.deviceId}
                                  className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-white/[0.06] dark:bg-[#18181b]"
                                >
                                  <div
                                    className={cn(
                                      'flex h-8 w-8 items-center justify-center rounded-lg',
                                      action.on
                                        ? 'bg-amber-50 text-amber-500 dark:bg-amber-500/10'
                                        : 'bg-gray-100 text-gray-500 dark:bg-zinc-800'
                                    )}
                                  >
                                    <Icon className="h-4 w-4" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {device.name ?? device.humanId}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-zinc-500">
                                      {device.type} · {device.provider}
                                    </div>
                                  </div>
                                  <Toggle
                                    label=""
                                    checked={action.on}
                                    onChange={(checked) =>
                                      handleToggleAction(index, actionIndex, checked)
                                    }
                                    size="sm"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAction(index, actionIndex)}
                                    className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {isError && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            {isEditing
              ? 'Failed to update virtual device. Please try again.'
              : 'Failed to create virtual device. Please try again.'}
          </div>
        )}
      </form>

      {showAddDeviceModal !== null && (
        <Modal
          isOpen
          onClose={() => {
            setShowAddDeviceModal(null);
            setSearchQuery('');
          }}
          title="Add Device"
          subtitle="Select a device to add to this position"
          size="md"
        >
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search devices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white dark:placeholder-zinc-500"
            />
          </div>

          <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-white/[0.06]">
            {devicesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
              </div>
            ) : filteredDevices.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-500 dark:text-zinc-500">
                  {switchableDevices.length === 0
                    ? 'No switchable devices available'
                    : 'No devices match your search'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                {filteredDevices.map((device) => {
                  const Icon = DEVICE_ICONS[device.type ?? 'switch'] ?? Power;
                  const isOn =
                    device.state?.light?.on ?? device.state?.switch?.on ?? device.state?.fan?.on;

                  return (
                    <button
                      key={device.id}
                      onClick={() => handleAddAction(showAddDeviceModal, device)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                    >
                      <div
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-lg',
                          isOn
                            ? 'bg-amber-50 text-amber-500 dark:bg-amber-500/10'
                            : 'bg-gray-100 text-gray-500 dark:bg-zinc-800'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {device.name ?? device.humanId ?? device.id}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-zinc-500">
                          {device.type} · {device.provider}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>
      )}
    </SlideOutPanel>
  );
}

// Routed Device Form
const routedDeviceSchema = z.object({
  humanId: z
    .string()
    .min(1, 'Required')
    .regex(/^[a-z0-9_-]+$/, 'Only lowercase letters, numbers, hyphens, and underscores'),
  name: z.string().min(1, 'Required'),
  type: z.string().min(1, 'Required'),
  propertyRoutes: z.array(
    z.object({
      property: z.string().min(1),
      targetDeviceIds: z.array(z.string()).min(1, 'At least one device required'),
    })
  ),
  commandRoutes: z.array(
    z.object({
      command: z.string().min(1),
      targetDeviceIds: z.array(z.string()).min(1, 'At least one device required'),
    })
  ),
});

type RoutedDeviceFormData = z.infer<typeof routedDeviceSchema>;

function RoutedDeviceForm({ isOpen, onClose, onSuccess, onBack, virtualDevice }: FormProps) {
  const queryClient = useQueryClient();
  const { data: devicesData } = useListDevices();
  const devices = useMemo(() => devicesData?.devices ?? [], [devicesData?.devices]);

  const [propertyRoutesExpanded, setPropertyRoutesExpanded] = useState(true);
  const [commandRoutesExpanded, setCommandRoutesExpanded] = useState(true);
  const isEditing = !!virtualDevice;

  const createMutation = usePostApiVirtualDevices();
  const updateMutation = usePutApiVirtualDevicesId();

  // Parse existing config for edit mode
  const existingConfig = useMemo(() => {
    if (!virtualDevice?.config) return null;
    const config = virtualDevice.config as {
      propertyRoutes?: Record<string, string[]>;
      commandRoutes?: Record<string, string[]>;
    };
    return config;
  }, [virtualDevice?.config]);

  // Build default values from existing device or use empty defaults
  const defaultValues = useMemo((): RoutedDeviceFormData => {
    if (isEditing && existingConfig) {
      return {
        humanId: virtualDevice.humanId ?? '',
        name: virtualDevice.name ?? '',
        type: virtualDevice.type ?? '',
        propertyRoutes: Object.entries(existingConfig.propertyRoutes ?? {}).map(
          ([property, targetDeviceIds]) => ({
            property,
            targetDeviceIds,
          })
        ),
        commandRoutes: Object.entries(existingConfig.commandRoutes ?? {}).map(
          ([command, targetDeviceIds]) => ({
            command,
            targetDeviceIds,
          })
        ),
      };
    }
    return {
      humanId: '',
      name: '',
      type: '',
      propertyRoutes: [],
      commandRoutes: [],
    };
  }, [isEditing, existingConfig, virtualDevice]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<RoutedDeviceFormData>({
    resolver: zodResolver(routedDeviceSchema),
    defaultValues,
  });

  const {
    fields: propertyFields,
    append: appendProperty,
    remove: removeProperty,
  } = useFieldArray({
    control,
    name: 'propertyRoutes',
  });

  const {
    fields: commandFields,
    append: appendCommand,
    remove: removeCommand,
  } = useFieldArray({
    control,
    name: 'commandRoutes',
  });

  const selectedType = watch('type');
  const propertyRoutes = watch('propertyRoutes');
  const commandRoutes = watch('commandRoutes');

  // Get available properties for the selected device type
  const availableProperties = useMemo(() => {
    if (!selectedType) return [];
    const used = new Set(propertyRoutes.map((r) => r.property));
    return (DEVICE_PROPERTIES[selectedType] ?? []).filter((p) => !used.has(p));
  }, [selectedType, propertyRoutes]);

  // Get available commands (not already routed)
  const availableCommands = useMemo(() => {
    const used = new Set(commandRoutes.map((r) => r.command));
    return DEVICE_COMMANDS.filter((c) => !used.has(c));
  }, [commandRoutes]);

  const getDeviceById = useCallback(
    (deviceId: string) => devices.find((d) => d.id === deviceId),
    [devices]
  );

  const onSubmit = async (data: RoutedDeviceFormData) => {
    // Build the config with the new structure
    const propertyRoutesObj: Record<string, string[]> = {};
    for (const route of data.propertyRoutes) {
      propertyRoutesObj[route.property] = route.targetDeviceIds;
    }

    const commandRoutesObj: Record<string, string[]> = {};
    for (const route of data.commandRoutes) {
      commandRoutesObj[route.command] = route.targetDeviceIds;
    }

    const config = {
      propertyRoutes: propertyRoutesObj,
      commandRoutes: commandRoutesObj,
    };

    try {
      if (isEditing && virtualDevice?.id) {
        await updateMutation.mutateAsync({
          id: virtualDevice.id,
          data: {
            name: data.name,
            config,
          },
        });
      } else {
        await createMutation.mutateAsync({
          data: {
            humanId: data.humanId,
            name: data.name,
            type: data.type,
            kind: 'routed',
            config,
          },
        });
      }
      queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetApiVirtualDevicesQueryKey() });
      onSuccess();
    } catch {
      // Error handled by mutation state
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isError = createMutation.isError || updateMutation.isError;

  const typeOptions = [
    { value: 'switch', label: 'Switch' },
    { value: 'light', label: 'Light' },
    { value: 'fan', label: 'Fan' },
    { value: 'window_covering', label: 'Window covering' },
    { value: 'speaker', label: 'Speaker' },
    { value: 'tv', label: 'TV' },
  ];

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Routed Device' : 'Create Routed Device'}
      subtitle={
        isEditing
          ? 'Modify property and command routes for this device'
          : 'Route properties and commands to different underlying devices'
      }
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit(onSubmit)} loading={isPending}>
            {isEditing ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      {onBack && (
        <div className="mb-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to type selection
          </button>
        </div>
      )}

      <form className="space-y-6">
        {/* Basic info */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Human ID"
            {...register('humanId')}
            placeholder="media-center"
            error={errors.humanId?.message}
            hint={isEditing ? 'Cannot be changed' : 'Unique identifier (lowercase, no spaces)'}
            disabled={isEditing}
          />
          <TextField
            label="Name"
            {...register('name')}
            placeholder="Media Center"
            error={errors.name?.message}
          />
        </div>

        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select
              label="Device Type"
              value={field.value}
              onChange={field.onChange}
              options={typeOptions}
              placeholder="Select type..."
              error={errors.type?.message}
              fullWidth
              disabled={isEditing}
            />
          )}
        />

        {selectedType && (
          <>
            {/* Property Routes Section */}
            <div className="rounded-xl border border-gray-200 dark:border-white/[0.06]">
              <button
                type="button"
                onClick={() => setPropertyRoutesExpanded(!propertyRoutesExpanded)}
                className="flex w-full items-center justify-between px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">Property Routes</span>
                  <Badge variant="default" size="sm">
                    {propertyFields.length}
                  </Badge>
                </div>
                {propertyRoutesExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {propertyRoutesExpanded && (
                <div className="border-t border-gray-200 p-4 dark:border-white/[0.06]">
                  <p className="mb-3 text-xs text-gray-500 dark:text-zinc-500">
                    Route properties to devices. Write operations go to all devices; state is read
                    from the first device.
                  </p>

                  {propertyFields.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center dark:border-white/[0.08]">
                      <p className="text-sm text-gray-500 dark:text-zinc-500">
                        No property routes configured. Properties without routes will not be
                        accessible.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {propertyFields.map((field, index) => (
                        <PropertyRouteRow
                          key={field.id}
                          index={index}
                          control={control}
                          devices={devices}
                          getDeviceById={getDeviceById}
                          onRemove={() => removeProperty(index)}
                          error={errors.propertyRoutes?.[index]}
                        />
                      ))}
                    </div>
                  )}

                  {availableProperties.length > 0 && (
                    <div className="mt-3">
                      <AddRouteDropdown
                        options={availableProperties}
                        onSelect={(prop) => appendProperty({ property: prop, targetDeviceIds: [] })}
                        label="Add Property Route"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Command Routes Section */}
            <div className="rounded-xl border border-gray-200 dark:border-white/[0.06]">
              <button
                type="button"
                onClick={() => setCommandRoutesExpanded(!commandRoutesExpanded)}
                className="flex w-full items-center justify-between px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">Command Routes</span>
                  <Badge variant="default" size="sm">
                    {commandFields.length}
                  </Badge>
                </div>
                {commandRoutesExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {commandRoutesExpanded && (
                <div className="border-t border-gray-200 p-4 dark:border-white/[0.06]">
                  <p className="mb-3 text-xs text-gray-500 dark:text-zinc-500">
                    Route commands to devices. Commands are executed on all selected devices.
                  </p>

                  {commandFields.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center dark:border-white/[0.08]">
                      <p className="text-sm text-gray-500 dark:text-zinc-500">
                        No command routes configured. Commands without routes will be ignored.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {commandFields.map((field, index) => (
                        <CommandRouteRow
                          key={field.id}
                          index={index}
                          control={control}
                          devices={devices}
                          getDeviceById={getDeviceById}
                          onRemove={() => removeCommand(index)}
                          error={errors.commandRoutes?.[index]}
                        />
                      ))}
                    </div>
                  )}

                  {availableCommands.length > 0 && (
                    <div className="mt-3">
                      <AddRouteDropdown
                        options={availableCommands}
                        onSelect={(cmd) => appendCommand({ command: cmd, targetDeviceIds: [] })}
                        label="Add Command Route"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            {isEditing
              ? 'Failed to update virtual device. Please try again.'
              : 'Failed to create virtual device. Please try again.'}
          </div>
        )}
      </form>
    </SlideOutPanel>
  );
}

// Property Route Row Component
interface RouteRowProps {
  index: number;
  control: Control<RoutedDeviceFormData>;
  devices: DevicesDevice[];
  getDeviceById: (id: string) => DevicesDevice | undefined;
  onRemove: () => void;
  error?: FieldErrors<RoutedDeviceFormData['propertyRoutes'][number]>;
}

function PropertyRouteRow({
  index,
  control,
  devices,
  getDeviceById,
  onRemove,
  error,
}: RouteRowProps) {
  const route = useWatch({ control, name: `propertyRoutes.${index}` as const });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-white/[0.06] dark:bg-[#18181b]">
      <div className="mb-2 flex items-center justify-between">
        <code className="rounded bg-gray-100 px-2 py-0.5 text-sm text-gray-800 dark:bg-zinc-800 dark:text-zinc-300">
          {route?.property}
        </code>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <Controller
        control={control}
        name={`propertyRoutes.${index}.targetDeviceIds`}
        render={({ field }) => (
          <MultiDeviceSelect
            value={field.value}
            onChange={field.onChange}
            devices={devices}
            getDeviceById={getDeviceById}
            error={error?.targetDeviceIds?.message}
          />
        )}
      />
    </div>
  );
}

interface CommandRouteRowProps {
  index: number;
  control: Control<RoutedDeviceFormData>;
  devices: DevicesDevice[];
  getDeviceById: (id: string) => DevicesDevice | undefined;
  onRemove: () => void;
  error?: FieldErrors<RoutedDeviceFormData['commandRoutes'][number]>;
}

function CommandRouteRow({
  index,
  control,
  devices,
  getDeviceById,
  onRemove,
  error,
}: CommandRouteRowProps) {
  const route = useWatch({ control, name: `commandRoutes.${index}` as const });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-white/[0.06] dark:bg-[#18181b]">
      <div className="mb-2 flex items-center justify-between">
        <code className="rounded bg-gray-100 px-2 py-0.5 text-sm text-gray-800 dark:bg-zinc-800 dark:text-zinc-300">
          {route?.command}
        </code>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <Controller
        control={control}
        name={`commandRoutes.${index}.targetDeviceIds`}
        render={({ field }) => (
          <MultiDeviceSelect
            value={field.value}
            onChange={field.onChange}
            devices={devices}
            getDeviceById={getDeviceById}
            error={error?.targetDeviceIds?.message}
          />
        )}
      />
    </div>
  );
}

// Multi-Device Select Component
interface MultiDeviceSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  devices: DevicesDevice[];
  getDeviceById: (id: string) => DevicesDevice | undefined;
  error?: string;
}

function MultiDeviceSelect({
  value,
  onChange,
  devices,
  getDeviceById,
  error,
}: MultiDeviceSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredDevices = useMemo(() => {
    if (!search.trim()) return devices;
    const query = search.toLowerCase();
    return devices.filter(
      (d) =>
        d.name?.toLowerCase().includes(query) ||
        d.humanId?.toLowerCase().includes(query) ||
        d.provider?.toLowerCase().includes(query)
    );
  }, [devices, search]);

  const toggleDevice = (deviceId: string) => {
    if (value.includes(deviceId)) {
      onChange(value.filter((id) => id !== deviceId));
    } else {
      onChange([...value, deviceId]);
    }
  };

  const selectedDevices = value.map((id) => getDeviceById(id)).filter(Boolean) as DevicesDevice[];

  return (
    <div ref={containerRef} className="relative">
      {/* Selected devices as chips */}
      {selectedDevices.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selectedDevices.map((device) => {
            const Icon = DEVICE_ICONS[device.type ?? 'switch'] ?? Power;
            return (
              <span
                key={device.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 py-0.5 pl-2 pr-1 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-300"
              >
                <Icon className="h-3 w-3" />
                {device.name ?? device.humanId}
                <button
                  type="button"
                  onClick={() => toggleDevice(device.id!)}
                  className="rounded-full p-0.5 hover:bg-blue-100 dark:hover:bg-blue-500/30"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors',
          error
            ? 'border-red-300 dark:border-red-500/50'
            : 'border-gray-200 dark:border-white/[0.08]',
          'bg-white dark:bg-zinc-900',
          'hover:border-gray-300 dark:hover:border-white/[0.12]'
        )}
      >
        <span className="text-gray-500 dark:text-zinc-500">
          {value.length === 0 ? 'Select devices...' : `${value.length} device(s) selected`}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 text-gray-400 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {error && (
        <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
          <div className="border-b border-gray-200 p-2 dark:border-white/[0.06]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search devices..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded border-0 bg-gray-50 py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-zinc-800 dark:text-white dark:placeholder-zinc-500"
              />
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            {filteredDevices.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-zinc-500">
                No devices found
              </div>
            ) : (
              filteredDevices.map((device) => {
                const isSelected = value.includes(device.id!);
                const Icon = DEVICE_ICONS[device.type ?? 'switch'] ?? Power;

                return (
                  <button
                    key={device.id}
                    type="button"
                    onClick={() => toggleDevice(device.id!)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                  >
                    <div
                      className={cn(
                        'flex h-5 w-5 items-center justify-center rounded border',
                        isSelected
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-gray-300 dark:border-white/[0.12]'
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <div
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-lg',
                        'bg-gray-100 text-gray-500 dark:bg-zinc-800'
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {device.name ?? device.humanId}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-zinc-500">
                        {device.type} · {device.provider}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Add Route Dropdown Component
interface AddRouteDropdownProps {
  options: string[];
  onSelect: (value: string) => void;
  label: string;
}

function AddRouteDropdown({ options, onSelect, label }: AddRouteDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative inline-block">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        leftIcon={<Plus className="h-3.5 w-3.5" />}
      >
        {label}
      </Button>

      {isOpen && (
        <div className="absolute left-0 z-50 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
          <div className="max-h-48 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                }}
                className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-zinc-300 dark:hover:bg-white/[0.02]"
              >
                <code className="text-xs">{option}</code>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
